if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      this.prependMedia = this.dataset.disablePrepend != 'true';
      if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();
      this.filteringOption = this.dataset.filteringOption;

      // Show-more control style: 'full_width' (drop-down of all extras) or 'arrow_toggle'
      // (windowed pair of arrows). Both read from the SAME image-cap/filtered-set core below.
      this.showMoreStyle = this.dataset.showMoreStyle || 'full_width';
      this.windowOffset = 0; // arrow_toggle: index of the first visible image in the filtered set

      this.setupColorFiltering();
      this.setupShowMore();
      this.setupMutationFallback();

      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    disconnectedCallback() {
      if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      if (this.formChangeHandler) document.removeEventListener('change', this.formChangeHandler);
      if (this.mutationObserver) this.mutationObserver.disconnect();
      if (this._swapTimer) window.clearTimeout(this._swapTimer);
    }

    // Filters gallery media by a #tag in the alt text (e.g. "#kelp-brown") matching the
    // selected variant's color option. Images with no #tag are treated as shared/always shown.
    //
    // Two independent signals drive this, since Shrine's own variant-change flow is not a single
    // synchronous event: (1) the native `change` event bubbling from the swatch/select itself,
    // read synchronously off the variant-picker's own `currentVariant` the instant it updates
    // (no network wait, so the gallery never shows stale images even momentarily), and (2) the
    // theme's `variant-change` pub/sub event, published only after Shrine's own section-rendering
    // fetch resolves (a few hundred ms later). Both call the same code, so whichever lands first
    // "wins" and the second is a harmless no-op repeat. A MutationObserver below is a further
    // fallback in case some other script reorders/replaces gallery children out from under us.
    setupColorFiltering() {
      this.colorOptionKey = this.dataset.colorOptionKey || '';
      this.applyColorFilter(this.dataset.currentColor || '', { resetActive: false });

      this.formChangeHandler = (event) => {
        const variantPicker = event.target.closest('variant-selects, variant-radios');
        if (!variantPicker) return;
        const pickerSection = variantPicker.dataset.originalSection || variantPicker.dataset.section;
        if (pickerSection !== this.dataset.section) return;
        this.reactToVariant(variantPicker.currentVariant);
      };
      document.addEventListener('change', this.formChangeHandler);

      if (typeof subscribe !== 'function') return;
      this.variantChangeUnsubscriber = subscribe('variant-change', (event) => {
        if (!event || !event.data || event.data.sectionId !== this.dataset.section) return;
        this.reactToVariant(event.data.variant);
      });
    }

    reactToVariant(variant) {
      if (!variant) return;
      const colorValue = this.colorOptionKey ? variant[this.colorOptionKey] : '';
      this.applyColorFilter(colorValue, { resetActive: true });
    }

    // Safety net: if the gallery's children get reordered or replaced by something outside our
    // control (e.g. Shrine's own "prepend the new variant's featured image" behavior), re-apply
    // the last known filter so we never end up stuck showing a stale/wrong set of images.
    setupMutationFallback() {
      const list = this.querySelector('.product__media-list');
      if (!list || typeof MutationObserver === 'undefined') return;
      this.mutationObserver = new MutationObserver(() => {
        if (this._applyingColorFilter) return;
        this.applyColorFilter(this.lastAppliedColor || '', { resetActive: true });
      });
      this.mutationObserver.observe(list, { childList: true });
    }

    normalizeColor(value) {
      return (value || '').toString().trim().toLowerCase().replace(/\s+/g, '-');
    }

    getMediaTags(altText) {
      const matches = (altText || '').toLowerCase().matchAll(/#([a-z0-9-]+)/g);
      return Array.from(matches, (match) => match[1]);
    }

    applyColorFilter(colorValue, { resetActive } = {}) {
      // Guards against the MutationObserver fallback re-entering while we're mutating the DOM
      // ourselves below (e.g. if the slider component's initPages()/update() touch childList).
      if (this._applyingColorFilter) return;
      this._applyingColorFilter = true;
      try {
        this.lastAppliedColor = colorValue || '';
        const normalizedColor = this.normalizeColor(colorValue);
        const matchesColor = (altText) => {
          if (!normalizedColor) return true;
          const tags = this.getMediaTags(altText);
          return tags.length === 0 || tags.includes(normalizedColor);
        };

        this.querySelectorAll('.product__media-item').forEach((slide) => {
          slide.classList.toggle('hidden', !matchesColor(slide.dataset.alt));
        });
        this.querySelectorAll('.slider-counter__link').forEach((dot) => {
          dot.classList.toggle('hidden', !matchesColor(dot.dataset.alt));
        });
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('.thumbnail-list__item').forEach((thumb) => {
            thumb.classList.toggle('hidden', !matchesColor(thumb.dataset.alt));
          });
        }

        // Grid-cap and the show-more button state must always update from the filter result
        // above, regardless of whether anything below (active-slide handoff, notifying the
        // vendor slider component) succeeds - so this runs first and unconditionally.
        this.updateGridCap({ collapse: resetActive });
        this.positionFavoritesBadge();

        if (resetActive) {
          try {
            const activeMedia = this.querySelector('.product__media-item.is-active');
            if (!activeMedia || activeMedia.classList.contains('hidden')) {
              const firstVisible = this.querySelector('.product__media-item:not(.hidden)');
              if (firstVisible) this.setActiveMedia(firstVisible.dataset.mediaId, false);
            }
          } catch (error) {
            console.error('media-gallery: failed to update active slide after filtering', error);
          }
        }

        this.refreshSliderComponents();
      } finally {
        this._applyingColorFilter = false;
      }
    }

    // Keeps the favorites count badge (Phase 1 favorites UI) pinned to the first VISIBLE media
    // item, so it follows variant filtering and Shrine's featured-image prepend. The badge is
    // rendered as a direct child of <media-gallery> and only shown once placed inside an image
    // container, so a JS failure just means no badge - never a mispositioned one.
    positionFavoritesBadge() {
      const badge = this.querySelector('.product-favorites-badge');
      if (!badge) return;
      const firstVisibleItem = this.querySelector('.product__media-item:not(.hidden)');
      const container = firstVisibleItem && firstVisibleItem.querySelector('.product-media-container');
      if (!container) {
        badge.classList.remove('product-favorites-badge--placed');
        return;
      }
      if (badge.parentElement !== container) container.appendChild(badge);
      badge.classList.add('product-favorites-badge--placed');
    }

    // Lets the (vendor) slider component recompute its internal pages/dots now that some slides
    // may be hidden. Each component is isolated in its own try/catch so a failure here (e.g. the
    // component not being in the state it expects) can never prevent the filtering/grid-cap logic
    // above from taking effect - that logic must always run first, this is best-effort on top.
    refreshSliderComponents() {
      [this.elements.viewer, this.elements.thumbnails].forEach((component) => {
        if (!component) return;
        try {
          if (component.initPages) component.initPages();
          if (component.update) component.update();
        } catch (error) {
          console.error('media-gallery: failed to refresh slider component after filtering', error);
        }
      });
    }

    // Desktop 2-column grid: caps the visible (post-filter) set to `cap` images. Two selectable
    // UIs sit on top of the SAME cap + filtered-set data: a full-width show-more/less toggle
    // (reveals ALL extras), or an arrow pair (slides a `cap`-sized window by 2). Resets whenever
    // the variant filter changes.
    setupShowMore() {
      if (this.showMoreStyle === 'arrow_toggle') {
        this.classList.add('media-gallery--arrows'); // scopes the per-item swap fade to this style
        this.arrowControl = this.querySelector('[data-show-more-arrows]');
        this.upArrow = this.querySelector('[data-arrow-up]');
        this.downArrow = this.querySelector('[data-arrow-down]');
        if (this.upArrow) this.upArrow.addEventListener('click', () => this.shiftWindow(-2));
        if (this.downArrow) this.downArrow.addEventListener('click', () => this.shiftWindow(2));
      } else {
        this.showMoreButton = this.querySelector('[data-show-more-media]');
        if (this.showMoreButton) {
          this.showMoreButton.addEventListener('click', this.toggleShowMore.bind(this));
        }
      }
      this.updateGridCap();
    }

    /* ---------------- shared cap / filtered-set core ---------------- */

    getCap() {
      // Cap comes from the section's "Desktop Gallery" editor settings via data-grid-cap
      return parseInt(this.dataset.gridCap, 10) || 4;
    }

    // The variant-filtered set: media items not hidden by the color filter (.hidden). Both
    // UIs window/cap THIS array by index.
    getFilteredItems() {
      return Array.from(this.querySelectorAll('.product__media-item')).filter(
        (slide) => !slide.classList.contains('hidden')
      );
    }

    updateGridCap({ collapse } = {}) {
      if (this.showMoreStyle === 'arrow_toggle') {
        this.updateArrowWindow({ reset: collapse });
        return;
      }

      const cap = this.getCap();
      const visibleSlides = this.getFilteredItems();
      visibleSlides.forEach((slide, index) => {
        slide.classList.toggle('product__media-item--grid-extra', index >= cap);
      });

      // A genuine variant change always resets the toggle back to collapsed, even if the new
      // set still exceeds the cap (otherwise a gallery left expanded on the old variant
      // would stay expanded after switching color, which reads as stale/wrong).
      if (collapse) this.collapseShowMore();

      if (!this.showMoreButton) return;

      const needsButton = visibleSlides.length > cap;
      this.showMoreButton.classList.toggle('product-media-show-more--needed', needsButton);
      if (!needsButton) this.collapseShowMore();
    }

    /* ---------------- Style 1: full-width show-more/less (animated) ---------------- */

    setShowMoreExpanded(expanded) {
      if (!this.showMoreButton) return;
      this.showMoreButton.classList.toggle('product-media-show-more--active', expanded);
      const label = this.showMoreButton.querySelector('[data-show-more-label]');
      if (label) {
        label.textContent = expanded
          ? this.showMoreButton.dataset.labelLess
          : this.showMoreButton.dataset.labelMore;
      }
    }

    toggleShowMore() {
      if (this.classList.contains('media-gallery--show-all')) this.closeShowMore();
      else this.openShowMore();
    }

    // Smooth drop-down: measure collapsed vs expanded list height (synchronously, no paint
    // between) and animate `height` while the extras fade in, clipped by --animating's
    // overflow:hidden — so the extras grow into view instead of just appearing.
    openShowMore() {
      this.setShowMoreExpanded(true);
      const list = this.querySelector('.product__media-list');
      const extras = Array.from(this.querySelectorAll('.product__media-item--grid-extra'));
      if (!list || !extras.length || !window.matchMedia('(min-width: 990px)').matches) {
        this.classList.add('media-gallery--show-all');
        return;
      }
      const startHeight = list.offsetHeight;
      this.classList.add('media-gallery--show-all'); // extras -> display:block; list at full height
      const endHeight = list.offsetHeight;
      this.classList.add('media-gallery--animating');
      extras.forEach((el) => { el.style.opacity = '0'; });
      list.style.height = startHeight + 'px';
      void list.offsetHeight; // reflow so the start values are committed before we change them
      list.style.height = endHeight + 'px';
      extras.forEach((el) => { el.style.opacity = '1'; });
      this.afterHeightTransition(list, () => {
        this.classList.remove('media-gallery--animating');
        list.style.height = '';
        extras.forEach((el) => { el.style.opacity = ''; });
      });
    }

    closeShowMore() {
      this.setShowMoreExpanded(false);
      const list = this.querySelector('.product__media-list');
      const extras = Array.from(this.querySelectorAll('.product__media-item--grid-extra'));
      if (!list || !extras.length || !window.matchMedia('(min-width: 990px)').matches) {
        this.classList.remove('media-gallery--show-all');
        return;
      }
      const startHeight = list.offsetHeight; // expanded
      // Read the collapsed height by toggling show-all off/on synchronously (no paint between).
      this.classList.remove('media-gallery--show-all');
      const endHeight = list.offsetHeight; // collapsed
      this.classList.add('media-gallery--show-all'); // keep extras visible while they animate out
      this.classList.add('media-gallery--animating');
      list.style.height = startHeight + 'px';
      extras.forEach((el) => { el.style.opacity = '1'; });
      void list.offsetHeight; // reflow
      list.style.height = endHeight + 'px';
      extras.forEach((el) => { el.style.opacity = '0'; });
      this.afterHeightTransition(list, () => {
        this.classList.remove('media-gallery--animating');
        this.classList.remove('media-gallery--show-all'); // NOW hide the extras (display:none)
        list.style.height = '';
        extras.forEach((el) => { el.style.opacity = ''; });
      });
    }

    // Instant (non-animated) collapse — used on variant change / when the button becomes
    // unneeded, where an animated collapse would read as laggy. Also clears any in-flight
    // animation's inline styles.
    collapseShowMore() {
      this.classList.remove('media-gallery--show-all', 'media-gallery--animating');
      const list = this.querySelector('.product__media-list');
      if (list) list.style.height = '';
      this.querySelectorAll('.product__media-item--grid-extra').forEach((el) => {
        el.style.opacity = '';
      });
      this.setShowMoreExpanded(false);
    }

    afterHeightTransition(el, callback) {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        el.removeEventListener('transitionend', onEnd);
        callback();
      };
      const onEnd = (event) => {
        if (event.target === el && event.propertyName === 'height') finish();
      };
      el.addEventListener('transitionend', onEnd);
      window.setTimeout(finish, 500); // fallback in case transitionend never fires (> 0.4s duration)
    }

    /* ---------------- Style 2: arrow toggle (windowed) ---------------- */

    // Applies the current window to the filtered set and syncs the arrow buttons' state.
    updateArrowWindow({ reset } = {}) {
      const cap = this.getCap();
      const filtered = this.getFilteredItems();
      const maxOffset = Math.max(0, filtered.length - cap);
      if (reset) this.windowOffset = 0;
      this.windowOffset = Math.min(Math.max(0, this.windowOffset), maxOffset);
      this.renderWindow(filtered, cap);
      if (this.arrowControl) {
        this.arrowControl.classList.toggle('product-media-arrows--needed', filtered.length > cap);
      }
    }

    renderWindow(filtered, cap) {
      const start = this.windowOffset;
      const end = start + cap;
      filtered.forEach((slide, index) => {
        slide.classList.toggle('product__media-item--window-hidden', index < start || index >= end);
      });
      // Variant-filtered-out items are display:none already; don't leave stale window flags on them.
      this.querySelectorAll('.product__media-item.hidden').forEach((slide) => {
        slide.classList.remove('product__media-item--window-hidden');
      });
      this.updateArrowDisabled(filtered.length, cap);
    }

    updateArrowDisabled(total, cap) {
      const atStart = this.windowOffset <= 0;
      const atEnd = this.windowOffset >= Math.max(0, total - cap);
      if (this.upArrow) this.upArrow.disabled = atStart;
      if (this.downArrow) this.downArrow.disabled = atEnd || total <= cap;
    }

    // Slides the window by `delta` (±2), clamped. Swaps in place: the retained images just
    // reflow to their new slots and the newly-ENTERING images fade in (opacity 0 -> 1). No
    // whole-list opacity fade (that flashed the page background white), and scroll position is
    // preserved so the viewport never jumps.
    shiftWindow(delta) {
      const cap = this.getCap();
      const filtered = this.getFilteredItems();
      const maxOffset = Math.max(0, filtered.length - cap);
      const next = Math.min(Math.max(0, this.windowOffset + delta), maxOffset);
      if (next === this.windowOffset) return;

      const prevStart = this.windowOffset;
      const prevEnd = prevStart + cap;
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;

      this.windowOffset = next;
      this.renderWindow(filtered, cap); // swap the window in place (display + disabled states)

      // Fade in only the images that just entered the window (weren't in the previous window),
      // so carried-over images don't flicker.
      const entering = filtered
        .slice(next, next + cap)
        .filter((slide, i) => {
          const absoluteIndex = next + i;
          return absoluteIndex < prevStart || absoluteIndex >= prevEnd;
        });
      entering.forEach((slide) => { slide.style.opacity = '0'; });
      void this.offsetHeight; // commit opacity:0 before transitioning back
      window.requestAnimationFrame(() => {
        entering.forEach((slide) => { slide.style.opacity = ''; });
      });

      // Undo any scroll the display swap / focus change may have triggered — expand in place.
      window.scrollTo({ top: scrollY, left: scrollX, behavior: 'instant' });
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
      if (filtering && currentVariant) {
        const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link');
        allSlides.forEach(slide => {
          if (slide.dataset.alt === currentVariant[this.filteringOption] || slide.dataset.alt === 'always_display') {
            slide.classList.remove('hidden');
          } else {
            slide.classList.add('hidden');
          }
        })
      }

      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend && this.prependMedia) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (filtering && currentVariant) {
        if (this.elements.viewer && this.elements.viewer.initPages) this.elements.viewer.initPages();
        if (this.elements.viewer && this.elements.viewer.update) this.elements.viewer.update();
      }

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, false, 10, true)) return;

      if (this.elements.thumbnails.vertical) {
        console.log('setActiveThumbnail')
        this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop });
      } else {
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
